import styled from 'styled-components';

// Construct the styled components wrapper for the img element
interface ImageWrapperProps {
  rounded: boolean;
  w: string | number;
  h?: string | number;
  border?: string;
  visible: boolean;
  borderRadius?: string;
}

export const ImageWrapper = styled.div<ImageWrapperProps>`
  display: inline-block;
  width: ${({ w }) => w};
  height: ${({ h }) => h};
  border-radius: ${({ rounded, borderRadius }) => (rounded ? '50%' : borderRadius ? borderRadius : '')};
  -webkit-border-radius: ${({ rounded, borderRadius }) => (rounded ? '50%' : borderRadius ? borderRadius : '')};
  overflow: ${({ rounded, borderRadius }) => (rounded || borderRadius ? 'hidden' : '')};
  border: ${({ rounded, border }) => (rounded && border ? border : '')};
  position: relative;
  ${({ visible }) => !visible && 'visibility: hidden;'}
`;

interface ImgProps {
  padding?: string;
  backgroundColor?: string;
  rounded?: boolean;
  borderRadius?: string;
}

export const Img = styled.img<ImgProps>`
  box-sizing: border-box;
  padding: ${({ padding }) => padding};
  background-color: ${({ backgroundColor }) => backgroundColor};
  &:-moz-loading {
    color: ${({ backgroundColor }) => backgroundColor};
  }
  border-radius: ${({ rounded, borderRadius }) => (rounded ? '50%' : borderRadius ? borderRadius : '')};
  -webkit-border-radius: ${({ rounded, borderRadius }) => (rounded ? '50%' : borderRadius ? borderRadius : '')};
`;

// Construct the styled placeholder using several variables + backups
export const styledPlaceholder = (
  height: number | string | undefined,
  width: number | string,
  placeholderWidth: number | string | undefined,
  placeholderHeight: number | string | undefined,
  backgroundColor: string | undefined,
  ratio: string = '1:1',
  border: string | undefined,
) => {
  let h: number | string | undefined = placeholderHeight || height;
  if (h === undefined) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    h = retrieveHeightFromRatio(width, ratio); // TODO: this is likely a production bug
  }

  return styled.div`
    height: ${h};
    width: ${placeholderWidth || width};
    ${backgroundColor && `background-color: ${backgroundColor};`}
    border-radius: 8px;
    ${border && `border: ${border};`}
  `;
};

// Construct a height value from the width and ratio passed
const retrieveHeightFromRatio = (
  placeholderHeight: number | string | undefined,
  height: string | number | undefined,
  width: number | string,
  ratio: string = '1:1',
): number | string => {
  const h: number | string | undefined = placeholderHeight || height;
  if (typeof h !== 'undefined') {
    return h;
  }

  const ratioArr: string[] = ratio.split(':');
  const rWidth: number = parseFloat(ratioArr[0]);
  const rHeight: number = parseFloat(ratioArr[1]);

  if (typeof width === 'string') {
    // Since we're probably dealing with 'px' or '%' in the string, this separates
    // the number from the unit and stores both for use later on.
    const parsedWidth: string[] = width.replace(/'/g, '').split(/(\d+)/);
    const numValue: number = parseFloat(parsedWidth[1]);

    const unit: string = parsedWidth[2];
    return `${(numValue * rHeight) / rWidth}${unit || 'px'}`;
  } else {
    return (width * rHeight) / rWidth;
  }
};

interface PlaceholderProps {
  w: string | number;
  h?: string | number;
  placeholderWidth?: number | string;
  placeholderHeight?: number | string;
  backgroundColor?: string;
  border?: string;
  ratio?: string;
}

export const Placeholder = styled.div<PlaceholderProps>`
  height: ${({ placeholderHeight, h, w, ratio }) => retrieveHeightFromRatio(placeholderHeight, h, w, ratio)};
  width: ${({ placeholderWidth, w }) => placeholderWidth || w};
  background-color: ${({ backgroundColor }) => backgroundColor};
  border-radius: 8px;
  border: ${({ border }) => border};
`;
